import { useState } from "react";
import {
  BarChart,
  Bar,
  ComposedChart,
  Line,
  Legend,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import PageHeader from "@/components/base/PageHeader";
import Card, { SectionTitle } from "@/components/base/Card";
import StatCard from "@/components/base/StatCard";
import Badge from "@/components/base/Badge";
import Pagination from "@/components/base/Pagination";
import { LoadingState, ErrorState } from "@/components/base/StateViews";
import DateRangeBar from "@/components/feature/DateRangeBar";
import { useAsync } from "@/hooks/useAsync";
import { usePager } from "@/hooks/usePager";
import {
  getBudget,
  getByCategory,
  getByCycle,
  getCashflow,
  getOverview,
  type BudgetCharge,
  type ReportPeriod,
} from "@/services/reportService";
import { formatDate, formatMoney, plural } from "@/utils/format";
import { defaultPeriod, periodLabel, type Period } from "@/utils/period";
import { monthLabel } from "@/services/payrollService";

const PIE_COLORS = [
  "oklch(var(--primary-500))",
  "oklch(var(--accent-500))",
  "oklch(var(--secondary-500))",
  "oklch(var(--primary-300))",
  "oklch(var(--accent-300))",
  "oklch(var(--secondary-300))",
];

const TOOLTIP_STYLE = {
  borderRadius: 8,
  border: "1px solid oklch(var(--background-200))",
  background: "oklch(var(--background-50))",
  fontSize: 12,
};

const millions = (v: number) => (Math.abs(v) >= 1_000_000 ? `${Math.round(v / 100_000) / 10}M` : `${Math.round(v / 1000)}k`);
const pct = (v: number | null) => (v === null ? "—" : `${Math.round(v)} %`);

async function loadReports(period: ReportPeriod) {
  const [overview, byCycle, expenses, cashflow, budget] = await Promise.all([
    getOverview(period),
    getByCycle(period),
    getByCategory(period, "DEPENSE"),
    getCashflow(period),
    getBudget(period),
  ]);
  return { overview, byCycle, expenses, cashflow, budget };
}

/** État d'une charge face à son prévisionnel. */
function budgetTone(c: BudgetCharge): { tone: "success" | "secondary" | "danger" | "neutral"; label: string } {
  if (c.rate === null) return { tone: "neutral", label: "Sans budget" };
  if (c.rate > 100) return { tone: "danger", label: "Dépassé" };
  if (c.rate >= 90) return { tone: "secondary", label: "Presque atteint" };
  return { tone: "success", label: "Dans le budget" };
}

/**
 * Rapports financiers calculés par le serveur sur l'intervalle choisi (par défaut le mois en cours) :
 * synthèse, comparaison par cycle, structure des charges, évolution, prévu vs réalisé, recouvrement des frais,
 * masse salariale et analyse automatique.
 */
export default function ReportsPage() {
  const [period, setPeriod] = useState<Period>(defaultPeriod);
  const { data, loading, error, reload } = useAsync(() => loadReports(period), [period.from, period.to]);
  const budgetPager = usePager();

  const header = (
    <>
      <PageHeader
        title="Rapports financiers"
        subtitle="Synthèse, cycles, charges, évolution, prévu vs réalisé, recouvrement, masse salariale et analyse"
        icon="ri-line-chart-line"
      />
      <Card className="mb-5">
        <DateRangeBar value={period} onChange={setPeriod} />
      </Card>
    </>
  );

  if (error) {
    return (
      <div className="animate-fade-in">
        {header}
        <ErrorState message={error} onRetry={reload} />
      </div>
    );
  }
  if (loading || !data) {
    return (
      <div className="animate-fade-in">
        {header}
        <LoadingState label="Calcul des rapports..." />
      </div>
    );
  }

  const { overview, byCycle, expenses, cashflow, budget } = data;
  const label = periodLabel(period);
  const finance = overview.finance;
  const cycles = byCycle.cycles;

  // Structure des charges : une même charge est déclinée par cycle, on regroupe par nom.
  const expenseByName = Object.entries(
    expenses.reduce<Record<string, number>>((acc, c) => {
      acc[c.name] = (acc[c.name] ?? 0) + c.total;
      return acc;
    }, {}),
  )
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value);
  const topExpenses = expenseByName.slice(0, 6);

  const comparison = cycles.map((c) => ({ name: c.label, Recettes: c.finance.totalRecettes, Dépenses: c.finance.totalDepenses }));
  const evolution = cashflow.points.map((p) => ({
    name: cashflow.granularity === "month" ? monthLabel(p.period) : formatDate(p.period).slice(0, 6),
    Recettes: p.recettes,
    Dépenses: p.depenses,
    "Solde cumulé": p.cumulative,
  }));

  const payrollTotal = cycles.reduce((s, c) => s + c.monthlyPayroll, 0);
  const overruns = budget.charges.filter((c) => c.rate !== null && c.rate > 100);
  const budgetPage = budgetPager.slice(budget.charges, `${period.from}|${period.to}`);
  const biggestCycle = [...cycles].sort((a, b) => b.finance.totalRecettes - a.finance.totalRecettes)[0];
  const heaviest = expenseByName[0];
  const monthlyRevenue = budget.months > 0 ? finance.totalRecettes / budget.months : 0;
  const hasMovements = finance.totalRecettes > 0 || finance.totalDepenses > 0;

  const analysis = !hasMovements
    ? [`Aucune recette ni dépense enregistrée ${label}.`]
    : [
        `Période analysée : ${label}. ${
          biggestCycle && biggestCycle.finance.totalRecettes > 0
            ? `Le cycle ${biggestCycle.label} génère le plus de recettes (${formatMoney(biggestCycle.finance.totalRecettes)}).`
            : "Aucune recette sur la période."
        }`,
        `La marge s'établit à ${Math.round(finance.marge)} %, pour un résultat net de ${formatMoney(finance.resultat)}.`,
        heaviest
          ? `Le poste de dépense le plus lourd est « ${heaviest.name} » avec ${formatMoney(heaviest.value)}, soit ${Math.round((heaviest.value / finance.totalDepenses) * 100)} % des dépenses.`
          : "Aucune dépense sur la période.",
        budget.total.budget > 0
          ? `Les charges prévues sont consommées à ${pct(budget.total.rate)} (${formatMoney(budget.total.spent)} sur ${formatMoney(budget.total.budget)} prévus). ${
              overruns.length
                ? `Dépassement : ${overruns
                    .slice(0, 3)
                    .map((c) => `${c.name} (${pct(c.rate)})`)
                    .join(", ")}.`
                : "Aucune charge ne dépasse son prévisionnel."
            }`
          : "Aucun budget prévisionnel n'est défini pour les charges : renseignez-les dans « Dépenses › Charges & catégories ».",
        overview.fees.expected > 0
          ? `Recouvrement des frais ${overview.school.schoolYear ?? ""} : ${pct(overview.fees.collectionRate)} encaissés, ${formatMoney(overview.fees.outstanding)} restent à recouvrer.${
              overview.fees.collectionRate < 50 ? " Une relance des familles est recommandée." : ""
            }`
          : "Aucun frais attendu pour l'année active (fiche de renseignements à compléter).",
        payrollTotal > 0 && monthlyRevenue > 0
          ? `La masse salariale mensuelle (${formatMoney(payrollTotal)}) représente ${Math.round((payrollTotal / monthlyRevenue) * 100)} % des recettes mensuelles moyennes de la période.`
          : `Masse salariale mensuelle : ${formatMoney(payrollTotal)}.`,
        finance.resultat >= 0
          ? "La situation est excédentaire : les recettes couvrent l'ensemble des dépenses de la période."
          : "Attention : les dépenses dépassent les recettes sur la période. Une révision du budget est recommandée.",
      ];

  return (
    <div className="animate-fade-in">
      {header}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Total recettes" value={formatMoney(finance.totalRecettes)} icon="ri-arrow-up-circle-line" tone="primary" hint={label} />
        <StatCard label="Total dépenses" value={formatMoney(finance.totalDepenses)} icon="ri-arrow-down-circle-line" tone="accent" hint={label} />
        <StatCard label="Résultat" value={formatMoney(finance.resultat)} icon="ri-scales-3-line" tone="secondary" />
        <StatCard label="Marge" value={`${Math.round(finance.marge)} %`} icon="ri-percent-line" tone="primary" />
      </div>
      <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Budget consommé" value={pct(budget.total.rate)} icon="ri-pie-chart-line" tone="accent" hint={`${formatMoney(budget.total.spent)} / ${formatMoney(budget.total.budget)} prévus`} />
        <StatCard label="Taux de recouvrement" value={pct(overview.fees.collectionRate)} icon="ri-hand-coin-line" tone="primary" hint={`Année ${overview.school.schoolYear ?? "—"}`} />
        <StatCard label="Reste à recouvrer" value={formatMoney(overview.fees.outstanding)} icon="ri-time-line" tone="secondary" />
        <StatCard label="Masse salariale / mois" value={formatMoney(payrollTotal)} icon="ri-wallet-3-line" tone="primary" hint={`${plural(cycles.reduce((s, c) => s + c.payrollHeadcount, 0), "personne")}`} />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-5 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <SectionTitle icon="ri-bar-chart-2-line" title="Comparaison recettes vs dépenses par cycle" />
          <div className="h-[280px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={comparison} margin={{ top: 8, right: 8, left: 8, bottom: 0 }} barGap={10}>
                <CartesianGrid strokeDasharray="3 3" stroke="oklch(var(--background-300))" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 12, fill: "oklch(var(--foreground-500))" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "oklch(var(--foreground-400))" }} axisLine={false} tickLine={false} tickFormatter={millions} />
                <Tooltip formatter={(value: number) => formatMoney(value)} contentStyle={TOOLTIP_STYLE} />
                <Bar dataKey="Recettes" fill="oklch(var(--primary-500))" radius={[4, 4, 0, 0]} maxBarSize={56} />
                <Bar dataKey="Dépenses" fill="oklch(var(--accent-500))" radius={[4, 4, 0, 0]} maxBarSize={56} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card>
          <SectionTitle icon="ri-pie-chart-2-line" title="Structure des charges" subtitle="Répartition des dépenses" />
          {topExpenses.length === 0 ? (
            <p className="py-10 text-center text-xs text-foreground-400">Aucune dépense sur la période.</p>
          ) : (
            <>
              <div className="h-[220px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={topExpenses} dataKey="value" nameKey="name" innerRadius={45} outerRadius={80} paddingAngle={2}>
                      {topExpenses.map((_, i) => (
                        <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value: number) => formatMoney(value)} contentStyle={TOOLTIP_STYLE} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="mt-2 space-y-1.5">
                {topExpenses.map((c, i) => (
                  <div key={c.name} className="flex items-center justify-between gap-2 text-xs">
                    <span className="flex items-center gap-2 text-foreground-600">
                      <span className="w-2.5 h-2.5 rounded-full" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }}></span>
                      {c.name}
                    </span>
                    <span className="text-foreground-800">{formatMoney(c.value)}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </Card>
      </div>

      <Card className="mt-6">
        <SectionTitle
          icon="ri-line-chart-line"
          title="Évolution dans le temps"
          subtitle={cashflow.granularity === "month" ? "Par mois, avec le solde cumulé" : "Par jour, avec le solde cumulé"}
        />
        <div className="h-[300px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={evolution} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="oklch(var(--background-300))" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: "oklch(var(--foreground-500))" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "oklch(var(--foreground-400))" }} axisLine={false} tickLine={false} tickFormatter={millions} />
              <Tooltip formatter={(value: number) => formatMoney(value)} contentStyle={TOOLTIP_STYLE} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="Recettes" fill="oklch(var(--primary-400))" radius={[3, 3, 0, 0]} maxBarSize={28} />
              <Bar dataKey="Dépenses" fill="oklch(var(--accent-400))" radius={[3, 3, 0, 0]} maxBarSize={28} />
              <Line type="monotone" dataKey="Solde cumulé" stroke="oklch(var(--secondary-600))" strokeWidth={2} dot={false} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Card className="mt-6" padded={false}>
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-background-200 px-4 py-3">
          <div>
            <h3 className="text-sm font-semibold font-heading text-foreground-950">Prévu vs réalisé par charge</h3>
            <p className="text-xs text-foreground-500">
              Prévu = budget mensuel × {budget.months} mois couvert(s) par la période
            </p>
          </div>
          {budget.total.unassignedSpent > 0 && (
            <Badge tone="neutral" icon="ri-information-line">
              Hors charges : {formatMoney(budget.total.unassignedSpent)}
            </Badge>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] text-sm">
            <thead>
              <tr className="border-b border-background-200 text-left text-xs uppercase tracking-wide text-foreground-500">
                <th className="px-4 py-3 font-medium">Charge</th>
                <th className="px-4 py-3 font-medium text-right">Prévu</th>
                <th className="px-4 py-3 font-medium text-right">Réalisé</th>
                <th className="px-4 py-3 font-medium">Consommation</th>
                <th className="px-4 py-3 font-medium text-right">État</th>
              </tr>
            </thead>
            <tbody>
              {budgetPage.items.map((c) => {
                const state = budgetTone(c);
                const width = Math.min(100, c.rate ?? 0);
                return (
                  <tr key={c.chargeId} className="border-b border-background-200 last:border-0">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="w-7 h-7 rounded-md bg-accent-50 text-accent-700 flex items-center justify-center shrink-0">
                          <i className={`${c.icon} text-sm`}></i>
                        </span>
                        <div className="min-w-0">
                          <p className="text-foreground-900">{c.name}</p>
                          <p className="text-xs text-foreground-400">
                            {c.groupName}
                            {c.cycles.length > 1 &&
                              ` • ${c.cycles
                                .map((cy) => `${cycles.find((x) => x.cycleId === cy.cycleId)?.label ?? cy.cycleCode} ${pct(cy.rate)}`)
                                .join(" • ")}`}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-foreground-700">{formatMoney(c.budget)}</td>
                    <td className="px-4 py-3 text-right tabular-nums font-medium text-foreground-900">{formatMoney(c.spent)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="h-2 w-28 overflow-hidden rounded-full bg-background-200">
                          <div
                            className={`h-full rounded-full ${state.tone === "danger" ? "bg-accent-500" : state.tone === "secondary" ? "bg-secondary-500" : "bg-primary-600"}`}
                            style={{ width: `${width}%` }}
                          />
                        </div>
                        <span className="text-xs tabular-nums text-foreground-600">{pct(c.rate)}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Badge tone={state.tone}>{state.label}</Badge>
                    </td>
                  </tr>
                );
              })}
              {budget.charges.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-sm text-foreground-400">
                    Aucune charge avec un budget ou une dépense sur la période.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <Pagination pager={budgetPage} className="border-t border-background-200" />
      </Card>

      <div className="mt-6 grid grid-cols-1 gap-5 lg:grid-cols-2">
        <Card>
          <SectionTitle
            icon="ri-hand-coin-line"
            title="Recouvrement des frais"
            subtitle={`Année scolaire ${overview.school.schoolYear ?? "—"} (inscriptions non annulées)`}
          />
          <div className="space-y-3">
            {cycles.map((c) => (
              <div key={c.cycleId} className="rounded-md border border-background-200 bg-background-100/50 p-3.5">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-foreground-900">{c.label}</span>
                  <Badge tone={c.fees.collectionRate >= 75 ? "success" : c.fees.collectionRate >= 50 ? "secondary" : "danger"}>
                    {pct(c.fees.collectionRate)}
                  </Badge>
                </div>
                <div className="mt-2 h-2 overflow-hidden rounded-full bg-background-200">
                  <div className="h-full rounded-full bg-primary-600" style={{ width: `${Math.min(100, c.fees.collectionRate)}%` }} />
                </div>
                <div className="mt-2 grid grid-cols-3 gap-2 text-xs text-foreground-500">
                  <span>Attendu {formatMoney(c.fees.expected)}</span>
                  <span>Encaissé {formatMoney(c.fees.collected)}</span>
                  <span className="text-right">Reste {formatMoney(c.fees.outstanding)}</span>
                </div>
              </div>
            ))}
          </div>
        </Card>

        <Card>
          <SectionTitle icon="ri-wallet-3-line" title="Masse salariale mensuelle" subtitle="Personnel (salaire) + enseignants (heures × 4 × tarif)" />
          <div className="space-y-3">
            {cycles.map((c) => (
              <div key={c.cycleId} className="rounded-md border border-background-200 bg-background-100/50 p-3.5">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-foreground-900">{c.label}</span>
                  <span className="text-sm font-semibold tabular-nums text-foreground-900">{formatMoney(c.monthlyPayroll)}</span>
                </div>
                <div className="mt-2 flex flex-wrap justify-between gap-2 text-xs text-foreground-500">
                  <span>Enseignants {formatMoney(c.teacherPayroll)}</span>
                  <span>Personnel {formatMoney(c.staffPayroll)}</span>
                  <span>{plural(c.payrollHeadcount, "personne")}</span>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-5 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <SectionTitle icon="ri-sticky-note-line" title="Analyse automatique" subtitle="Lecture synthétique de la période" />
          <ul className="space-y-3">
            {analysis.map((line, i) => (
              <li key={i} className="flex items-start gap-3 text-sm text-foreground-700 leading-relaxed">
                <span className="mt-0.5 w-5 h-5 rounded-full bg-secondary-100 text-secondary-700 flex items-center justify-center shrink-0 text-xs font-semibold">
                  {i + 1}
                </span>
                {line}
              </li>
            ))}
          </ul>
        </Card>

        <Card>
          <SectionTitle icon="ri-flag-2-line" title="Résultat par cycle" />
          <div className="space-y-3">
            {cycles.map((c) => (
              <div key={c.cycleId} className="rounded-md border border-background-200 bg-background-100/50 p-3.5">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-foreground-900">{c.label}</span>
                  <Badge tone={c.finance.resultat >= 0 ? "success" : "danger"}>{c.finance.resultat >= 0 ? "Excédent" : "Déficit"}</Badge>
                </div>
                <p className={`mt-1.5 text-lg font-semibold font-heading ${c.finance.resultat >= 0 ? "text-primary-700" : "text-accent-700"}`}>
                  {formatMoney(c.finance.resultat)}
                </p>
                <div className="mt-2 flex items-center justify-between text-xs text-foreground-500">
                  <span>Recettes {formatMoney(c.finance.totalRecettes)}</span>
                  <span>Dépenses {formatMoney(c.finance.totalDepenses)}</span>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
